import React, { useMemo, useRef, useState } from 'react';
import { productImageUrl } from './AiLookResults';
import ProductInfoDialog from './ProductInfoDialog';

const laneOrder = ['exact', 'similar', 'budget', 'luxury'];

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function itemText(item) {
  return normalizeText([item.category, item.description].filter(Boolean).join(' '));
}

function isPrimaryGarment(item) {
  return /\b(dress|dresses|gown|abito|vestito|rochie|robe|kleid|vestido|jacket|giacca|jacheta|coat|blazer|shirt|camicia|camasa|tshirt|t-shirt|tee|maglietta|top|blouse|pants|pantaloni|trouser|jeans|blugi|denim|skirt|gonna|fusta)\b/.test(itemText(item));
}

function collectProducts(match) {
  const seen = new Set();
  const items = match?.items || [];
  const primaryItems = items.filter(isPrimaryGarment);
  const sourceItems = primaryItems.length > 0 ? primaryItems : items;

  return sourceItems
    .flatMap((item) =>
      laneOrder.flatMap((lane) =>
        (item.candidates?.[lane] || []).map((candidate) => ({
          ...candidate,
          lane,
          itemCategory: item.category,
        })),
      ),
    )
    .filter((candidate) => {
      if (!candidate?.id || !candidate.image_url) return false;
      const key = String(candidate.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 18);
}

export default function SimilarProductRail({ match, status, onStart, onShowAll, onTryOn, formatPrice, t }) {
  const railRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const products = useMemo(() => collectProducts(match), [match]);

  function scrollRail(direction) {
    const node = railRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * Math.max(280, node.clientWidth * 0.82), behavior: 'smooth' });
  }

  if (status === 'idle') {
    return (
      <section className="ct-similar-rail ct-similar-rail-empty">
        <div>
          <p className="ct-kicker">{t('similarRail.kicker')}</p>
          <h2>{t('similarRail.title')}</h2>
          <span>{t('similarRail.emptyCopy')}</span>
        </div>
        <button type="button" onClick={onStart}>
          <i className="ph ph-sparkle"></i>
          {t('similarRail.start')}
        </button>
      </section>
    );
  }

  if (status === 'loading' && products.length === 0) {
    return (
      <section className="ct-similar-rail ct-similar-rail-empty">
        <div>
          <p className="ct-kicker">{t('similarRail.kicker')}</p>
          <h2>{t('similarRail.title')}</h2>
          <span>{t('similarRail.loading')}</span>
        </div>
        <span className="ct-input-spinner"></span>
      </section>
    );
  }

  if (products.length === 0) return null;

  return (
    <section className="ct-similar-rail" aria-label={t('similarRail.title')}>
      <div className="ct-similar-rail-head">
        <h2>{t('similarRail.title')}</h2>
        <button type="button" onClick={onShowAll} aria-label={t('similarRail.showAll')}>
          <i className="ph ph-arrow-right"></i>
        </button>
      </div>
      <div className="ct-similar-rail-wrap">
        <button type="button" className="ct-similar-scroll previous" onClick={() => scrollRail(-1)} aria-label={t('common.previous')}>
          <i className="ph ph-caret-left"></i>
        </button>
        <div className="ct-similar-products" ref={railRef}>
          {products.map((product) => (
            <button
              type="button"
              className="ct-similar-product"
              onClick={() => setSelectedProduct(product)}
              key={product.id}
            >
              <span className="ct-similar-product-image">
                <img
                  src={productImageUrl(product.image_url)}
                  alt={product.name}
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
                <em>{t(`similarRail.lane.${product.lane}`)}</em>
              </span>
              <span className="ct-similar-product-copy">
                <strong>{product.name}</strong>
                <small>{product.store || product.brand || product.itemCategory}</small>
                <b>{formatPrice(product)}</b>
              </span>
            </button>
          ))}
          <button type="button" className="ct-similar-show-more" onClick={onShowAll}>
            {t('similarRail.showMore')}
          </button>
        </div>
        <button type="button" className="ct-similar-scroll next" onClick={() => scrollRail(1)} aria-label={t('common.next')}>
          <i className="ph ph-caret-right"></i>
        </button>
      </div>
      <ProductInfoDialog
        product={selectedProduct}
        formatPrice={formatPrice}
        onClose={() => setSelectedProduct(null)}
        onTryOn={onTryOn}
        t={t}
      />
    </section>
  );
}
